import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Plus, Loader2, Pencil, Trash2, Search, Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { API_PATH, request } from '../config/api';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import { useAuth } from '../store/AuthContext';
import { useConfirm } from '../store/ConfirmDialogContext';
import styles from './ProfanityPage.module.css';

const ProfanityPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const confirmFn = useConfirm();
    const [words, setWords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [wordInput, setWordInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [importing, setImporting] = useState(false);
    const [importMessage, setImportMessage] = useState(null); // { type: 'success' | 'error', text }
    const fileInputRef = useRef(null);

    const isSuperadmin = user?.role === 'superadmin';

    const filteredWords = React.useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return words;
        return words.filter((row) => row.word.toLowerCase().includes(q));
    }, [words, searchQuery]);

    const fetchWords = useCallback(async () => {
        if (!isSuperadmin) return;
        setLoading(true);
        setError(null);
        try {
            const res = await request(`${API_PATH}/superadmin/profanity`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'Failed to load list');
                setWords([]);
                return;
            }
            setWords(data.words || []);
        } catch (e) {
            setError('Network error');
            setWords([]);
        } finally {
            setLoading(false);
        }
    }, [isSuperadmin]);

    useEffect(() => {
        if (!isSuperadmin) {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchWords();
    }, [isSuperadmin, navigate, fetchWords]);

    const openAdd = () => {
        setEditingId(null);
        setWordInput('');
        setFormError('');
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id);
        setWordInput(row.word);
        setFormError('');
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
        setWordInput('');
        setFormError('');
    };

    const handleSave = async () => {
        const trimmed = wordInput.trim();
        if (!trimmed) {
            setFormError('Enter a word');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const url = editingId
                ? `${API_PATH}/superadmin/profanity/${editingId}`
                : `${API_PATH}/superadmin/profanity`;
            const method = editingId ? 'PATCH' : 'POST';
            const body = editingId ? { word: trimmed } : { word: trimmed };
            const res = await request(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setFormError(data.error || 'Failed to save');
                return;
            }
            closeModal();
            fetchWords();
        } catch (e) {
            setFormError('Network error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row) => {
        const ok = await confirmFn({
            title: 'Delete word',
            message: `Remove "${row.word}" from the profanity list?`,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            const res = await request(`${API_PATH}/superadmin/profanity/${row.id}`, { method: 'DELETE' });
            if (res.ok) {
                fetchWords();
            } else {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Failed to delete word');
            }
        } catch (e) {
            setError('Network error');
        }
    };

    const handleExport = () => {
        if (words.length === 0) return;
        const sheetData = [['Word'], ...words.map((row) => [row.word])];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        worksheet['!cols'] = [{ wch: 30 }];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Profanity words');
        const date = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `profanity-words_${date}.xlsx`);
    };

    const handleImportClick = () => {
        setImportMessage(null);
        fileInputRef.current?.click();
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file next time
        if (!file) return;

        setImporting(true);
        setImportMessage(null);
        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            if (!sheet) throw new Error('No sheet found in this file');

            // Read as array-of-arrays so we don't depend on a specific header name — just
            // take the first non-empty cell of each row and skip an optional "Word" header.
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
            const candidateWords = rows
                .map((r) => (Array.isArray(r) ? r[0] : r))
                .map((v) => (v == null ? '' : String(v).trim()))
                .filter((v) => v && v.toLowerCase() !== 'word');

            if (candidateWords.length === 0) {
                setImportMessage({ type: 'error', text: 'No words found in that file.' });
                return;
            }

            const res = await request(`${API_PATH}/superadmin/profanity/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ words: candidateWords }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setImportMessage({ type: 'error', text: data.error || 'Import failed.' });
                return;
            }
            setImportMessage({
                type: 'success',
                text: `Added ${data.added} word${data.added !== 1 ? 's' : ''}${data.skipped > 0 ? `, skipped ${data.skipped} duplicate${data.skipped !== 1 ? 's' : ''}` : ''}.`,
            });
            fetchWords();
        } catch (err) {
            console.error(err);
            setImportMessage({ type: 'error', text: err.message === 'No sheet found in this file' ? err.message : 'Could not read that file — make sure it’s a valid Excel or CSV file.' });
        } finally {
            setImporting(false);
        }
    };

    if (!isSuperadmin) return null;

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={Filter}
                title="Profanity filter"
                subtitle="Manage words to filter in participant responses. Add, edit, or remove words from the list, or import/export the whole list as an Excel file."
                actions={<div className={styles.headerActions}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleImportFile}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant="secondary"
                            onClick={handleImportClick}
                            icon={importing ? <Loader2 size={16} className={styles.spin} /> : <Upload size={16} />}
                            disabled={importing}
                        >
                            {importing ? 'Importing…' : 'Import'}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleExport}
                            icon={<Download size={16} />}
                            disabled={words.length === 0}
                        >
                            Export
                        </Button>
                        <Button variant="primary" onClick={openAdd} icon={<Plus size={18} />} className={styles.pillCta}>
                            Add word
                        </Button>
                    </div>}
            />

            {importMessage && (
                <div className={importMessage.type === 'error' ? styles.errorBanner : styles.successBanner} role="alert">
                    {importMessage.text}
                </div>
            )}

            {error && (
                <div className={styles.errorBanner} role="alert">
                    {error}
                </div>
            )}

            {loading ? (
                <div className={styles.loading}>
                    <Loader2 size={32} className={styles.spinner} />
                    Loading…
                </div>
            ) : (
                <div className={styles.tagsWrap}>
                    {words.length === 0 ? (
                        <div className={styles.emptyState}>
                            <Filter size={40} className={styles.emptyIcon} aria-hidden />
                            <p className={styles.emptyText}>No profanity words yet</p>
                            <p className={styles.emptyHint}>Add words to filter in participant responses.</p>
                            <Button variant="primary" size="sm" onClick={openAdd} icon={<Plus size={16} />} className={`${styles.emptyBtn} ${styles.pillCta}`}>
                                Add word
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className={styles.searchWrap}>
                                <Search size={18} className={styles.searchIcon} aria-hidden />
                                <input
                                    type="search"
                                    className={styles.searchInput}
                                    placeholder="Search words…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-label="Search profanity words"
                                />
                                {searchQuery.trim() && (
                                    <span className={styles.searchMeta}>
                                        {filteredWords.length} of {words.length}
                                    </span>
                                )}
                            </div>
                            {filteredWords.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <Search size={40} className={styles.emptyIcon} aria-hidden />
                                    <p className={styles.emptyText}>No matching words</p>
                                    <p className={styles.emptyHint}>
                                        {searchQuery.trim() ? `No words contain "${searchQuery.trim()}". Try a different search.` : 'Add words to filter in participant responses.'}
                                    </p>
                                </div>
                            ) : (
                                <ul className={styles.tagList} role="list">
                                    {filteredWords.map((row) => (
                                        <li key={row.id} className={styles.tagItem}>
                                            <span className={styles.tagLabel}>{row.word}</span>
                                            <div className={styles.tagActions}>
                                                <button
                                                    type="button"
                                                    className={styles.tagActionBtn}
                                                    onClick={() => openEdit(row)}
                                                    aria-label={`Edit ${row.word}`}
                                                    title="Edit"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.tagActionBtn} ${styles.tagActionBtnDanger}`}
                                                    onClick={() => handleDelete(row)}
                                                    aria-label={`Remove ${row.word}`}
                                                    title="Remove"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            )}

            <Modal
                isOpen={modalOpen}
                onClose={closeModal}
                title={editingId ? 'Edit word' : 'Add word'}
                size="sm"
            >
                <div className={styles.form}>
                    <label className={styles.label}>Word</label>
                    <Input
                        type="text"
                        value={wordInput}
                        onChange={(e) => setWordInput(e.target.value)}
                        placeholder="Enter word to filter"
                        disabled={saving}
                        autoFocus
                    />
                    {formError && <p className={styles.formError} role="alert">{formError}</p>}
                    <div className={styles.modalFooter}>
                        <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleSave} disabled={saving} icon={saving ? <Loader2 size={16} className={styles.spin} /> : null}>
                            {saving ? 'Saving…' : editingId ? 'Update' : 'Add'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ProfanityPage;
